import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../lib/auth-context';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  ArrowLeft, 
  Mail, 
  MessageSquare, 
  HelpCircle, 
  Shield, 
  AlertTriangle,
  ExternalLink,
  Send,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { projectId, publicAnonKey } from '/utils/supabase/info';
import { analytics } from '../lib/analytics';
import { StockHeroStrip, StockImage, WarmMarketingBackdrop } from '../components/marketing/MarketingVisuals';
import { ConsumerAICopilot } from '../components/ConsumerAICopilot';

export default function Support() {
  const navigate = useNavigate();
  const { user, accessToken } = useAuth();
  const [category, setCategory] = useState<'general' | 'billing' | 'technical' | 'abuse' | 'refund'>('general');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState(typeof user?.email === 'string' ? user.email : '');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    analytics.track('support_page_viewed');
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !subject || !message) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/support`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${publicAnonKey}`,
            ...(accessToken ? { 'X-User-Token': accessToken } : {}),
          },
          body: JSON.stringify({
            category,
            subject,
            message,
            email,
            userAgent: navigator.userAgent,
            url: window.location.href,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to submit support request');
      }

      analytics.track('support_request_submitted', { category });
      setSubmitted(true);
      toast.success('Support request submitted! We\'ll respond within 24-48 hours.');
    } catch (error) {
      console.error('Error submitting support request:', error);
      toast.error('Failed to submit request. Please email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <WarmMarketingBackdrop>
      <div className="min-h-screen">
        <div className="max-w-2xl mx-auto px-6 py-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-6"
            aria-label="Go back"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          <Card className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-medium mb-3">Request Received</h2>
            <p className="text-neutral-600 mb-6">
              Thank you for contacting TENSION support. We've received your request and will respond to{' '}
              <strong>{email}</strong> within 24-48 hours.
            </p>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => navigate('/home')} variant="outline">
                Return Home
              </Button>
              <Button onClick={() => { setSubmitted(false); setSubject(''); setMessage(''); }}>
                Submit Another Request
              </Button>
            </div>
          </Card>
        </div>
      </div>
      <ConsumerAICopilot context="support" />
      </WarmMarketingBackdrop>
    );
  }

  return (
    <WarmMarketingBackdrop>
    <div className="min-h-screen">
      <div className="relative mx-auto max-w-5xl px-6 pt-6">
        <div className="grid gap-6 overflow-hidden rounded-3xl border border-violet-200/50 bg-white/80 shadow-lg md:grid-cols-[1.1fr_0.9fr] md:items-center">
          <div className="space-y-3 p-8 md:p-10">
            <h1 className="text-4xl font-light tracking-wide text-neutral-900">Support Center</h1>
            <p className="text-lg text-neutral-600">
              We&apos;re here to help. Get answers to common questions or contact us directly.
            </p>
          </div>
          <div className="relative min-h-[200px]">
            <StockImage
              stockKey="supportCalm"
              width={800}
              height={480}
              alt="Friendly support — illustrative stock photo"
              className="h-full min-h-[200px] w-full object-cover object-[center_20%] md:rounded-none md:rounded-r-3xl"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-white/80 to-transparent" aria-hidden />
          </div>
        </div>
        <StockHeroStrip stockKey="handsWalking" alt="We are here for you — illustrative imagery" className="mt-6" />
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
          aria-label="Go back"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="space-y-8">
          {/* Quick Links */}
          <div className="grid md:grid-cols-3 gap-4">
            <Card 
              className="p-6 cursor-pointer hover:border-violet-300 transition-colors"
              onClick={() => navigate('/faq')}
            >
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-blue-100">
                  <HelpCircle className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-medium mb-1">FAQ</h3>
                  <p className="text-sm text-neutral-600">
                    Frequently asked questions
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-violet-100">
                  <Mail className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-medium mb-1">Email Us</h3>
                  <a 
                    href="mailto:support@tension.app"
                    className="text-sm text-purple-600 hover:underline"
                  >
                    support@tension.app
                  </a>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-green-100">
                  <MessageSquare className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-medium mb-1">Response Time</h3>
                  <p className="text-sm text-neutral-600">
                    Within 24-48 hours
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Contact Form */}
          <Card className="p-8">
            <h2 className="text-2xl font-medium mb-6">Submit a Support Request</h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Category Selection */}
              <div>
                <Label>Request Category</Label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-2">
                  {[
                    { value: 'general', label: 'General', icon: MessageSquare },
                    { value: 'billing', label: 'Billing', icon: Shield },
                    { value: 'technical', label: 'Technical', icon: AlertTriangle },
                    { value: 'abuse', label: 'Report Abuse', icon: AlertTriangle },
                    { value: 'refund', label: 'Refund', icon: ExternalLink },
                  ].map((cat) => (
                    <button
                      key={cat.value}
                      type="button"
                      onClick={() => setCategory(cat.value as any)}
                      className={`p-3 rounded-lg border-2 transition-colors text-left ${
                        category === cat.value
                          ? 'border-violet-500 bg-violet-50'
                          : 'border-neutral-200 hover:border-violet-300'
                      }`}
                    >
                      <cat.icon className={`w-4 h-4 mb-1 ${category === cat.value ? 'text-violet-600' : 'text-neutral-400'}`} />
                      <div className={`text-sm font-medium ${category === cat.value ? 'text-violet-900' : ''}`}>
                        {cat.label}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="email">Your Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="mt-2"
                />
                <p className="text-xs text-neutral-500 mt-1">
                  We'll respond to this email address
                </p>
              </div>

              {/* Subject */}
              <div>
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Brief description of your issue"
                  required
                  maxLength={200}
                  className="mt-2"
                />
              </div>

              {/* Message */}
              <div>
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Please provide as much detail as possible..."
                  required
                  rows={6}
                  maxLength={2000}
                  className="mt-2"
                />
                <p className="text-xs text-neutral-500 mt-1">
                  {message.length}/2000 characters
                </p>
              </div>

              {/* Category-specific info */}
              {category === 'billing' && (
                <div className="tension-glow p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Billing Support:</strong> Please include your account email and any relevant transaction IDs or order numbers.
                  </p>
                </div>
              )}

              {category === 'refund' && (
                <div className="tension-glow p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">
                    <strong>Refund Policy:</strong> Refunds are available within 30 days of purchase for subscription plans. Please include your reason for requesting a refund.
                  </p>
                </div>
              )}

              {category === 'abuse' && (
                <div className="tension-glow p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">
                    <strong>Report Abuse:</strong> If you're experiencing relationship abuse, please contact the National Domestic Violence Hotline: 1-800-799-7233. For app-related abuse reports, provide as much detail as possible.
                  </p>
                </div>
              )}

              <Button type="submit" disabled={submitting} className="w-full" size="lg">
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Request
                  </>
                )}
              </Button>
            </form>
          </Card>

          {/* Additional Resources */}
          <Card className="p-6">
            <h3 className="font-medium mb-4">Additional Resources</h3>
            <div className="space-y-3">
              <a
                href="/privacy"
                className="flex items-center justify-between p-3 rounded-lg hover:bg-neutral-50 transition-colors"
              >
                <span className="text-sm">Privacy Policy</span>
                <ExternalLink className="w-4 h-4 text-neutral-400" />
              </a>
              <a
                href="/terms"
                className="flex items-center justify-between p-3 rounded-lg hover:bg-neutral-50 transition-colors"
              >
                <span className="text-sm">Terms of Service</span>
                <ExternalLink className="w-4 h-4 text-neutral-400" />
              </a>
              <a
                href="/about"
                className="flex items-center justify-between p-3 rounded-lg hover:bg-neutral-50 transition-colors"
              >
                <span className="text-sm">About TENSION</span>
                <ExternalLink className="w-4 h-4 text-neutral-400" />
              </a>
            </div>
          </Card>
        </div>
      </div>
    </div>
    <ConsumerAICopilot context="support" />
    </WarmMarketingBackdrop>
  );
}
